import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLayout } from "./LayoutContext";
import Loader from "./Loader";
import UserLogin from "./UserLogin";
import EdencareGetStarted from "./EdencareGetStarted";
import { Constants } from "constants/api";
import { describeError, logger } from "utils/logger";

// Without a timeout a stalled request to the API left the page on a blurred
// spinner forever, which looks like a blank page on phones.
const VERIFY_TIMEOUT_MS = 15000;


const User: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { setLayoutData } = useLayout();
    const navigate = useNavigate();

    const [clientActive, setClientActive] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [clientData, setClientData] = useState(null);
    // Set when the API couldn't be reached (timeout / network), as opposed to
    // the API saying the client is inactive.
    const [loadError, setLoadError] = useState<string | null>(null);
    // To prevent state updates after unmount
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Stable verifyClient function
    const verifyClient = useCallback(async (slugValue: string) => {
        const startedAt = performance.now();
        setLoadError(null);
        setLoading(true);
        try {
            const formdata = new FormData();
            formdata.append("slug", slugValue);
            const { data } = await axios.post(Constants.API.VERIFY_CLIENT, formdata, {
                headers: { Accept: "application/json" },
                timeout: VERIFY_TIMEOUT_MS,
            });
            if (!isMounted.current) return;
            logger.info("client_verified", {
                slug: slugValue,
                active: !!data?.data?.status,
                durationMs: Math.round(performance.now() - startedAt),
            });

            if (data?.data?.status) {
                const clientData = data.data;
                setLayoutData({
                    clientLogo: clientData?.brand_image,
                    clientName: clientData?.client_name,
                });
                setClientData(clientData);
                localStorage.setItem("clientDetails", JSON.stringify(clientData));
                setClientActive(true);
            } else {
                localStorage.removeItem("clientDetails");
                setClientActive(false);
                setClientData(null);
            }
        } catch (error: any) {
            logger.error("client_verify_failed", {
                slug: slugValue,
                durationMs: Math.round(performance.now() - startedAt),
                httpStatus: error?.response?.status,
                timedOut: error?.code === "ECONNABORTED",
                error: describeError(error),
            });
            if (isMounted.current) {
                setClientActive(false);
                // An HTTP answer means the server rejected the client; anything
                // else (timeout, offline, blocked) is a connection problem.
                if (!error?.response) {
                    setLoadError(
                        error?.code === "ECONNABORTED"
                            ? "The server is taking too long to respond."
                            : "We couldn't reach the server."
                    );
                }
            }
            localStorage.removeItem("clientDetails");
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [setLayoutData]);

    //  Verify client once when slug changes
    useEffect(() => {
        if (!slug) {
            setLoading(false);
            return;
        }
        verifyClient(slug);
    }, [slug, verifyClient]);

    // Navigation logic after render
    useEffect(() => {
        if (loading) return;

        if (clientActive && isLoggedIn) {
            navigate("/scan", { replace: true });
        } else if (!clientActive && isLoggedIn) {
            localStorage.removeItem("clientDetails");
            localStorage.removeItem("userDetails");
            navigate("/login", { replace: true });
        }
        // An active client without a logged-in user stays on /client/<slug>;
        // the start page is rendered below instead of redirecting to /login.
    }, [clientActive, isLoggedIn, loading, navigate]);

    const userLoginSuccess = () => setIsLoggedIn(true);

    if (loading) return <Loader />;

    if (loadError) {
        return (
            <div className="p-12 text-center bg-white rounded-xl shadow-lg">
                <h1 className="text-2xl font-bold text-gray-800">Unable to load</h1>
                <p className="mt-2 text-base text-gray-500">
                    {loadError} Please check your internet connection and try again.
                </p>
                <button
                    className="mt-4 px-4 py-2 bg-red-700 text-white rounded"
                    onClick={() => slug && verifyClient(slug)}
                >
                    Try Again
                </button>
            </div>
        );
    }

    if (!clientActive && !isLoggedIn) {
        return (
            <div className="p-12 text-center bg-white rounded-xl shadow-lg">
                <h1 className="text-3xl font-bold text-gray-800">Client Not Active</h1>
                <p className="mt-2 text-base text-gray-500">
                    Please contact support for more information.
                </p>
                <button className="mt-4 px-4 py-2 bg-red-700 text-white rounded">
                    Contact Us
                </button>
            </div>
        );
    }

    if (clientActive && !isLoggedIn) {
        return clientData?.id == 49 ? <EdencareGetStarted /> : <UserLogin />;
    }

    return null;
};

export default User;
